import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { RetryableError } from '../../../shared/RetryableError';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { MappingDialog } from '../components/MappingDialog';
import { ExportErrorCode, exportErrorMessage, hasCode, isForbidden } from '../exportErrors';
import { formatDate } from '../exportFormat';
import { CODE_MAPPING_BANNER } from '../exportOptions';
import {
  categoryLabel, type AccountingMapping, type ChartAccount, type CodeBulkAction, type CodeBulkPreview, type CodeBulkResult, type CodeKind, type CodeMappingStatus,
  type CodeReadiness, type CodeReadinessItem, type EligiblePayroll,
} from '../exportTypes';
import { accountingGateway } from '../index';
import { useExportCapabilities } from '../useExportCapabilities';
import '../../job-roles/job-roles.css';
import '../accounting-export.css';

type LoadState = { kind: 'loading' } | { kind: 'ready'; data: CodeReadiness } | { kind: 'forbidden' } | { kind: 'error'; message: string };

const PAGE_SIZE = 50;
const KINDS: { key: CodeKind; label: string; catalog: 'deductions' | 'earnings' | 'benefits'; noun: string }[] = [
  { key: 'Deduction', label: 'Deductions', catalog: 'deductions', noun: 'deduction' },
  { key: 'Earning', label: 'Earnings', catalog: 'earnings', noun: 'earning' },
  { key: 'Benefit', label: 'Benefits', catalog: 'benefits', noun: 'benefit' },
];
const STATUSES: CodeMappingStatus[] = ['Unmapped', 'Mapped', 'Ambiguous'];
const STATUS_BADGE: Record<CodeMappingStatus, { glyph: string; className: string }> = {
  Mapped: { glyph: '✔', className: 'ax-badge--ok' },
  Unmapped: { glyph: '○', className: 'ax-badge--neutral' },
  Ambiguous: { glyph: '⚠', className: 'ax-badge--warn' },
};
const ACTION_BADGE: Record<CodeBulkAction, { glyph: string; className: string; word: string }> = {
  Create: { glyph: '+', className: 'ax-badge--ok', word: 'Create' },
  Replace: { glyph: '↻', className: 'ax-badge--warn', word: 'Replace' },
  Skip: { glyph: '–', className: 'ax-badge--neutral', word: 'Skip' },
  Conflict: { glyph: '✖', className: 'ax-badge--danger', word: 'Conflict' },
};
const TAX_TREATMENT: Record<string, string> = { PreTax: 'Pre-tax', PostTax: 'Post-tax' };

const keyOf = (c: Pick<CodeReadinessItem, 'kind' | 'codeId'>) => `${c.kind}|${c.codeId}`;
const todayIso = () => new Date().toISOString().slice(0, 10);
/** A fresh idempotency key: letters, digits and `-` only, well within the server's 100-character limit. */
const newIdempotencyKey = (): string => `bulk-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`}`;

type Step = 'configure' | 'preview' | 'done';

/**
 * Map the selected codes to ONE account: choose the account and date, PREVIEW what would be created, replaced, skipped or refused, then confirm. Nothing is written
 * until the confirmation box is ticked and Apply is pressed; an existing mapping is only replaced when "Replace existing mappings" is on.
 */
function BulkMapDialog({ selected, accounts, onClose, onApplied }: {
  selected: CodeReadinessItem[];
  accounts: ChartAccount[];
  onClose: () => void;
  onApplied: (result: CodeBulkResult) => void;
}) {
  const uid = useId();
  const [step, setStep] = useState<Step>('configure');
  const [accountId, setAccountId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [preview, setPreview] = useState<CodeBulkPreview | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState<CodeBulkResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // One key per previewed request: a retry of the SAME apply is idempotent, a new preview gets a new key.
  const keyRef = useRef(newIdempotencyKey());
  const idOf = (k: string) => `${uid}-${k}`;
  const account = accounts.find((a) => a.id === accountId);
  const request = { codes: selected.map((c) => ({ kind: c.kind, codeId: c.codeId })), chartAccountId: accountId, effectiveFrom, replaceExisting };

  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of selected) counts.set(c.accountingCategory, (counts.get(c.accountingCategory) ?? 0) + 1);
    return [...counts.entries()].map(([category, n]) => `${categoryLabel(category)} (${n})`).join(', ');
  }, [selected]);

  async function runPreview(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!accountId) return setError('Choose the account the selected codes map to.');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom)) return setError('Enter the date the mappings take effect.');
    setBusy(true);
    try {
      const p = await accountingGateway.previewCodeBulk(request);
      keyRef.current = newIdempotencyKey();
      setPreview(p);
      setConfirmed(false);
      setStep('preview');
    } catch (err) {
      setError(exportErrorMessage(err, 'The preview could not be built.'));
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    if (!preview || !confirmed) return;
    setBusy(true);
    setError(null);
    try {
      const r = await accountingGateway.applyCodeBulk({ ...request, idempotencyKey: keyRef.current });
      setResult(r);
      setStep('done');
      onApplied(r);
    } catch (err) {
      setError(hasCode(err, ExportErrorCode.IdempotencyKeyReused) ? 'This request was already used for a different set of codes. Go back, preview again and apply.' : exportErrorMessage(err, 'The mappings could not be applied. Nothing was changed.'));
    } finally {
      setBusy(false);
    }
  }

  const changes = preview ? preview.create + preview.replace : 0;
  const canApply = !!preview && confirmed && changes > 0 && preview.errors.length === 0 && !busy;

  return (
    <ModalDialog
      title="Map selected codes" size="xl" busy={busy} onClose={onClose}
      footer={step === 'done' ? (
        <button type="button" className="jr-btn jr-btn--primary" onClick={onClose} data-autofocus>Close</button>
      ) : (
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          {step === 'preview' && <button type="button" className="jr-btn jr-btn--secondary" onClick={() => { setStep('configure'); setError(null); }} disabled={busy}>Back</button>}
          {step === 'configure'
            ? <button type="submit" form={idOf('form')} className="jr-btn jr-btn--primary" disabled={busy} data-testid="bulk-preview">{busy ? 'Building preview…' : 'Preview changes'}</button>
            : <button type="button" className="jr-btn jr-btn--primary" onClick={() => void apply()} disabled={!canApply} data-testid="bulk-apply">{busy ? 'Applying…' : `Apply ${changes} ${changes === 1 ? 'mapping' : 'mappings'}`}</button>}
        </>
      )}
    >
      <div className="ax-banner" role="note"><p>{CODE_MAPPING_BANNER}</p></div>
      {error && <p className="jr-error jr-error--block" role="alert" data-testid="bulk-error">{error}</p>}

      {step === 'configure' && (
        <form id={idOf('form')} onSubmit={(e) => void runPreview(e)} noValidate className="ax-form">
          <p>{selected.length} code{selected.length === 1 ? '' : 's'} selected: {categoryCounts}. They will all be mapped to the one account you choose. Nothing is saved until you have seen the preview and confirmed it.</p>
          <div className="jr-grid">
            <div className="jr-field">
              <label htmlFor={idOf('account')}>Account <span aria-hidden="true" className="jr-req">*</span></label>
              <select id={idOf('account')} value={accountId} onChange={(e) => setAccountId(e.target.value)} required data-autofocus data-testid="bulk-account">
                <option value="">Choose an account</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.accountNumber}: {a.name} ({a.accountType})</option>)}
              </select>
              <p className="jr-hint">Only active accounts from your chart of accounts are listed. A custom or unknown code is never mapped to an account for you.</p>
            </div>
            <div className="jr-field">
              <label htmlFor={idOf('date')}>Effective from <span aria-hidden="true" className="jr-req">*</span></label>
              <input id={idOf('date')} type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} required data-testid="bulk-date" />
            </div>
          </div>
          <label className="ax-check ax-check--top">
            <input type="checkbox" checked={replaceExisting} onChange={(e) => setReplaceExisting(e.target.checked)} data-testid="bulk-replace" aria-describedby={idOf('replace-hint')} />
            <span><strong>Replace existing mappings.</strong> <span id={idOf('replace-hint')} className="jr-hint">Off by default: a code that already has a different mapping is left alone and shown as a conflict. When on, that mapping is ended (kept as history) and replaced.</span></span>
          </label>
        </form>
      )}

      {step === 'preview' && preview && (
        <div data-testid="bulk-preview-panel">
          <p role="status" data-testid="bulk-totals">
            <strong>{preview.create} to create, {preview.replace} to replace, {preview.skip} skipped, {preview.conflict} in conflict.</strong>{' '}
            Account {account ? `${account.accountNumber} ${account.name}` : ''}, effective {formatDate(effectiveFrom)}.
          </p>
          <p data-testid="bulk-replace-state" className={replaceExisting ? 'jr-warning' : 'jr-hint'}>
            Replace existing mappings: <strong>{replaceExisting ? 'ON. Existing mappings for the codes marked Replace will be ended and replaced.' : 'OFF. No existing mapping will be changed.'}</strong>
          </p>
          {preview.errors.length > 0 && <ul className="jr-error jr-error--block" role="alert">{preview.errors.map((m) => <li key={m}>{m}</li>)}</ul>}
          <div className="jr-table-scroll" role="region" aria-label="Bulk mapping preview" tabIndex={0}>
            <table className="jr-table" data-testid="bulk-preview-table">
              <caption className="ax-caption">What applying would do, code by code</caption>
              <thead><tr><th scope="col">Code</th><th scope="col">Name</th><th scope="col">Outcome</th><th scope="col">Current mapping</th><th scope="col">Note</th></tr></thead>
              <tbody>
                {preview.items.map((i) => {
                  const badge = ACTION_BADGE[i.action];
                  return (
                    <tr key={keyOf(i)} data-action={i.action}>
                      <th scope="row">{i.code ?? i.codeId}</th>
                      <td>{i.name ?? ''}</td>
                      <td><span className={`ax-badge ${badge.className}`}><span aria-hidden="true">{badge.glyph}</span> {badge.word}</span></td>
                      <td>{i.existingAccount ? `${i.existingAccount.accountNumber} ${i.existingAccount.accountName}` : 'None'}</td>
                      <td>{i.reason ?? (i.action === 'Create' ? 'No mapping yet.' : i.action === 'Replace' ? 'The current version is ended and kept as history.' : '')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="ax-confirm">
            <label className="ax-check ax-check--top">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="bulk-confirm" disabled={changes === 0 || preview.errors.length > 0} />
              <span>
                I confirm that {preview.create} mapping{preview.create === 1 ? '' : 's'} will be created and {preview.replace} replaced{replaceExisting ? '' : ' (replacing is off)'}. This is recorded in the audit log, and the journal does not use code mappings yet.
              </span>
            </label>
          </div>
          {changes === 0 && preview.errors.length === 0 && <p className="jr-hint">Nothing would change, so there is nothing to apply.</p>}
        </div>
      )}

      {step === 'done' && result && (
        <p className="jr-notice jr-notice--ok" role="status" data-testid="bulk-result">
          {result.replayed ? 'This request had already been applied, so nothing new was written. ' : ''}
          Created {result.created}, replaced {result.replaced}, skipped {result.skipped}, conflicts left alone {result.conflicts}.
        </p>
      )}
    </ModalDialog>
  );
}

/**
 * Code mappings: the company's deduction, earning and benefit codes with the account each is mapped to. In Phase 1 these mappings are RECORDED AND AUDITED but the journal cannot
 * use them (the payroll supplies no per-code amounts), which the banner says permanently. Mapping many codes to one account is a bulk flow with a preview and a required confirmation.
 */
export function CodeMappingsTab() {
  const { caps } = useExportCapabilities();
  const canManage = caps?.canManageMappings !== false;
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [reloadNonce, setReloadNonce] = useState(0);
  const [kind, setKind] = useState<CodeKind | ''>('');
  const [status, setStatus] = useState<CodeMappingStatus | ''>('');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [runId, setRunId] = useState('');
  const [payrolls, setPayrolls] = useState<EligiblePayroll[]>([]);
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);
  const [selected, setSelected] = useState<Map<string, CodeReadinessItem>>(new Map());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [single, setSingle] = useState<{ item: CodeReadinessItem; replaces: AccountingMapping | null } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setPage(1); }, 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    accountingGateway.listChartAccounts({ active: true, pageSize: 500 }, controller.signal).then((p) => { if (!controller.signal.aborted) setAccounts(p.items); }).catch(() => undefined);
    accountingGateway.listEligiblePayrolls(undefined, controller.signal).then((l) => { if (!controller.signal.aborted) setPayrolls(l.items); }).catch(() => undefined);
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    accountingGateway
      .getCodeReadiness({ ...(kind ? { kind } : {}), ...(debounced ? { search: debounced } : {}), ...(runId ? { payrollRunId: runId } : {}), page, pageSize: PAGE_SIZE }, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setState({ kind: 'ready', data }); })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setState(isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: exportErrorMessage(err, 'Could not load the code mappings.') });
      });
    return () => controller.abort();
  }, [kind, debounced, runId, page, reloadNonce]);

  if ((caps && !caps.canPreview) || state.kind === 'forbidden') {
    return (
      <section className="ax-tab" aria-labelledby="ax-cm-title">
        <h3 id="ax-cm-title">Code mappings</h3>
        <AccessDeniedNotice requiredPermissionLabel="permission to view GL mappings" />
      </section>
    );
  }

  const data = state.kind === 'ready' ? state.data : null;
  const rows = (data?.items ?? []).filter((c) => !status || c.mappingStatus === status);
  const totalPages = data ? Math.max(1, Math.ceil(data.totalCount / data.pageSize)) : 1;
  const allShownSelected = rows.length > 0 && rows.every((r) => selected.has(keyOf(r)));
  const unavailable = data ? KINDS.filter((k) => data.catalogs[k.catalog] === 'Unavailable') : [];

  const toggle = (item: CodeReadinessItem, on: boolean) => setSelected((prev) => {
    const next = new Map(prev);
    if (on) next.set(keyOf(item), item); else next.delete(keyOf(item));
    return next;
  });
  const toggleAll = (on: boolean) => setSelected((prev) => {
    const next = new Map(prev);
    for (const r of rows) { if (on) next.set(keyOf(r), r); else next.delete(keyOf(r)); }
    return next;
  });

  async function openSingle(item: CodeReadinessItem) {
    setRowError(null);
    let replaces: AccountingMapping | null = null;
    if (item.mapping) {
      try {
        replaces = (await accountingGateway.listMappings({ category: item.accountingCategory, activeOnly: true, pageSize: 200 })).items.find((m) => m.id === item.mapping!.id) ?? null;
      } catch (err) {
        setRowError(exportErrorMessage(err, 'The current mapping could not be read, so it cannot be replaced right now.'));
        return;
      }
    }
    setSingle({ item, replaces });
  }

  return (
    <section className="ax-tab" aria-labelledby="ax-cm-title">
      <h3 id="ax-cm-title">Code mappings</h3>
      <div className="ax-banner" role="note" data-testid="code-mapping-banner"><p>{CODE_MAPPING_BANNER}</p></div>
      <p className="muted ax-lead">
        Map a deduction, earning or benefit code to a general ledger account now, so the mappings are ready when the payroll supplies per-code amounts. A saved mapping never changes how a
        payroll is calculated, taxed or exported today. Codes and mappings contain no employee information.
      </p>
      {notice && <p className="jr-notice jr-notice--ok" role="status" data-testid="code-notice">{notice}</p>}
      {rowError && <p className="jr-error jr-error--block" role="alert">{rowError}</p>}
      {caps && !caps.canManageMappings && <p className="jr-readonly" role="note">You can view code mappings, but changing them needs permission to manage GL mappings.</p>}

      {unavailable.map((k) => (
        <p key={k.key} className="jr-warning" role="note" data-testid={`catalog-unavailable-${k.catalog}`}>
          <span aria-hidden="true">⚠ </span>The {k.noun} code list could not be read, so {k.noun} codes are not shown. Mappings already saved are not affected.{' '}
          <button type="button" className="ax-link-btn" onClick={() => { setState({ kind: 'loading' }); setReloadNonce((n) => n + 1); }}>Try again</button>
        </p>
      ))}

      <div className="ax-toolbar" role="search" aria-label="Filter codes">
        <div className="jr-field">
          <label htmlFor="ax-cm-kind">Kind</label>
          <select id="ax-cm-kind" value={kind} onChange={(e) => { setKind(e.target.value as CodeKind | ''); setPage(1); }} data-testid="code-kind">
            <option value="">All kinds</option>
            {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
          </select>
        </div>
        <div className="jr-field">
          <label htmlFor="ax-cm-status">Mapping status</label>
          <select id="ax-cm-status" value={status} onChange={(e) => setStatus(e.target.value as CodeMappingStatus | '')} data-testid="code-status">
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="jr-field">
          <label htmlFor="ax-cm-search">Search by code or name</label>
          <input id="ax-cm-search" type="search" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="code-search" />
        </div>
        <div className="jr-field">
          <label htmlFor="ax-cm-run">Count elections for payroll</label>
          <select id="ax-cm-run" value={runId} onChange={(e) => { setRunId(e.target.value); setPage(1); }} data-testid="code-payroll">
            <option value="">No payroll (do not count)</option>
            {payrolls.map((p) => <option key={p.payrollRunId} value={p.payrollRunId}>{formatDate(p.checkDate)}, {p.payScheduleLabel}</option>)}
          </select>
        </div>
      </div>

      {canManage && selected.size > 0 && (
        <div className="ax-bulkbar" data-testid="bulk-bar">
          <p role="status"><strong>{selected.size}</strong> code{selected.size === 1 ? '' : 's'} selected</p>
          <button type="button" className="jr-btn jr-btn--primary jr-btn--small" onClick={() => setBulkOpen(true)} data-testid="bulk-open">Map selected codes…</button>
          <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => setSelected(new Map())}>Clear selection</button>
        </div>
      )}

      {state.kind === 'loading' && <p role="status" className="muted">Loading codes…</p>}
      {state.kind === 'error' && <RetryableError message={state.message} onRetry={() => { setState({ kind: 'loading' }); setReloadNonce((n) => n + 1); }} />}
      {data && rows.length === 0 && (
        <p className="muted" data-testid="no-codes">
          {unavailable.length === KINDS.length ? 'No codes could be listed because no code list is available right now.' : kind || status || debounced ? 'No codes match these filters.' : 'There are no active codes to map.'}
        </p>
      )}

      {data && rows.length > 0 && (
        <div className="jr-table-scroll" role="region" aria-label="Code mappings table" tabIndex={0}>
          <table className="jr-table jr-table--actions ax-table-wide" data-testid="codes-table">
            <caption className="ax-caption">Deduction, earning and benefit codes ({data.totalCount}). Mappings here are recorded but not used by the journal yet.</caption>
            <thead>
              <tr>
                {canManage && (
                  <th scope="col" className="ax-col-check">
                    <input type="checkbox" checked={allShownSelected} onChange={(e) => toggleAll(e.target.checked)} aria-label="Select every code shown" data-testid="select-all-codes" />
                  </th>
                )}
                <th scope="col">Code</th>
                <th scope="col">Name</th>
                <th scope="col">Kind</th>
                <th scope="col">Posts under</th>
                <th scope="col">Mapping</th>
                <th scope="col" className="num">Employees with an election</th>
                <th scope="col">Amount in payroll</th>
                {canManage && <th scope="col">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const badge = STATUS_BADGE[c.mappingStatus];
                return (
                  <tr key={keyOf(c)} data-testid={`code-row-${c.codeId}`}>
                    {canManage && (
                      <td className="ax-col-check">
                        <input type="checkbox" checked={selected.has(keyOf(c))} onChange={(e) => toggle(c, e.target.checked)} aria-label={`Select ${c.name}`} />
                      </td>
                    )}
                    <th scope="row">{c.code ?? '—'}</th>
                    <td>
                      {c.name}
                      {c.taxTreatment && <span className="ax-date jr-hint">{TAX_TREATMENT[c.taxTreatment]} (informational: never recalculated)</span>}
                    </td>
                    <td>{c.kind}{c.source && <span className="ax-date jr-hint">{c.source} side</span>}</td>
                    <td>{categoryLabel(c.accountingCategory)}</td>
                    <td>
                      <span className={`ax-badge ${badge.className}`} data-status={c.mappingStatus}><span aria-hidden="true">{badge.glyph}</span> {c.mappingStatus}</span>
                      {c.mapping && <span className="ax-date"><strong>{c.mapping.accountNumber}</strong> {c.mapping.accountName}</span>}
                      {c.mapping?.effectiveFrom && <span className="ax-date jr-hint">From {formatDate(c.mapping.effectiveFrom)}{c.mapping.version !== null ? `, version ${c.mapping.version}` : ''}</span>}
                    </td>
                    <td className="num">{c.employeesWithElection === null ? '—' : c.employeesWithElection}</td>
                    <td>{c.totalAmountInPayroll === null ? <span className="jr-hint" title={c.unavailableReason ?? undefined}>Not supplied yet</span> : c.totalAmountInPayroll}</td>
                    {canManage && (
                      <td>
                        <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => void openSingle(c)} aria-label={`${c.mapping ? 'Change the mapping for' : 'Map'} ${c.name}`}>
                          {c.mapping ? 'Change' : 'Map'}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && data.totalCount > data.pageSize && (
        <nav className="jr-pager" aria-label="Pagination">
          <span>Page {data.page} of {totalPages}</span>
          <span className="jr-pager__nav">
            <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={data.page <= 1}>Previous</button>
            <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={data.page >= totalPages}>Next</button>
          </span>
        </nav>
      )}
      <p className="jr-hint">A mapping is versioned: replacing one ends the old version and keeps it as history. Each save is recorded in the audit log with counts only.</p>

      {bulkOpen && (
        <BulkMapDialog
          selected={[...selected.values()]} accounts={accounts} onClose={() => setBulkOpen(false)}
          onApplied={(r) => {
            setSelected(new Map());
            setNotice(`Bulk mapping applied: ${r.created} created, ${r.replaced} replaced, ${r.skipped} skipped, ${r.conflicts} conflicts left alone. The journal does not use code mappings yet.`);
            setReloadNonce((n) => n + 1);
          }}
        />
      )}
      {single && (
        <MappingDialog
          title={`Map ${single.item.name}`} prefill={{
            category: single.item.accountingCategory, payrollCode: single.item.payrollCode, lockCategory: true, effectiveFrom: todayIso(),
            subject: `${single.item.name} (${single.item.kind.toLowerCase()} code${single.item.code ? ` ${single.item.code}` : ''})`,
          }}
          accounts={accounts} employees={null} departments={null} replaces={single.replaces} onClose={() => setSingle(null)}
          onSaved={(saved) => {
            const name = single.item.name;
            setSingle(null);
            setNotice(`Saved a mapping for ${name} (version ${saved.mappingVersion}). The journal does not use it yet.`);
            setReloadNonce((n) => n + 1);
          }}
        />
      )}
    </section>
  );
}
